import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons/faCircleCheck';
import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons/faCircleExclamation';
import { faCirclePlay } from '@fortawesome/pro-regular-svg-icons/faCirclePlay';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, CardContent, Grid, Stack, Typography } from '@mui/material';
import { APP_DEFAULT_EXPORT_PAGE_SIZE, APP_PAGE_SIZE_MEDIUM } from 'app/appConstants';
import { MprcCard, MprcChipChoice, MprcContainer, MprcIconButtonMoreOptionsDropdown, MprcJoinComponents, MprcListItemTitle, MprcRequestErrorReport, MprcRequestIndicator, MprcSpinnerIndicator, MprcText } from 'components';
import { MprcDropdownBuildGroupedItems, MprcDropdownItems } from 'components/MprcIconButtonDropdown';
import { MprcRequestSnackbarErrorReport } from 'components/MprcRequestErrorReport';
import { EnrollStatus, TrainingIndexTrainingClassification, TrainingIndexTrainingDto, TrainingIndexTrainingDtoPaginatedList, TrainingIndexTrainingSummaryDto } from 'generated/CoreApi_v1';
import { Formaters } from 'lib/Formaters';
import HtmlUtils from 'lib/HtmlUtils';
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from 'providers/SessionProvider';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { LmsPaths } from 'routes/LmsRouter';
import { DashPagination } from 'views/dashboard/components/DashPagination';
import { DoExemptTraining } from 'views/lms/monitoring/actions/DoExemptTraining';
import { UndoExemptTraining } from 'views/lms/monitoring/actions/UndoExemptTraining';
import { EnrollmentDetailsDialog } from 'views/lms/monitoring/Supervisor/dialogs/EnrollmentDetailsDialog';
import { TrainingRegistrationDialog } from 'views/lms/student/Registration/TrainingRegistrationDialog';

////////////////////////////////////////////////////////////////////////////
interface TrainingIndexTrainingsFilter {
    classification: TrainingIndexTrainingClassification | null;
    pageNumber: number;
}
interface IProps {
    userId: string;
    userName?: string;
    topicId?: string;
}

type MenuOptions = "continue" | "review" | "selfSubscribe" | "managerSubscribe" | "doExempt" | "undoExempt";

////////////////////////////////////////////////////////////////////////////

export const TrainingIndexTrainings: React.FC<IProps> = ({ userId, userName, topicId }) => {

    const api = useCoreApi();
    const { t } = useTranslation("dashboard");
    const navigate = useNavigate();
    const session = useSession();

    const [dialog, setDialog] = useState<any>();
    const [viewData, setViewData] = useState<TrainingIndexTrainingDtoPaginatedList>();
    const [viewError, setViewError] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [downloading, setDownloading] = useState<boolean>(false);
    const [classifications, setClassifications] = useState<TrainingIndexTrainingSummaryDto[]>([]);
    const [filter, setFilter] = useState<TrainingIndexTrainingsFilter>({
        classification: null,
        pageNumber: 1
    });

    ///
    const handleExportExcel = async () => {
        setDownloading(true);
        return HtmlUtils.downloadBlobFromPromise(
            api.v1.dashboardTrainingIndex.exportUserTrainings({
                userId,
                topicId: topicId ?? null,
                pageNumber: 1,
                pageSize: APP_DEFAULT_EXPORT_PAGE_SIZE
            }, { format: "blob" })
                .catch(MprcRequestSnackbarErrorReport)
                .finally(() => setDownloading(false))
        );
    };

    ///
    const handleFilter = (filter: Partial<TrainingIndexTrainingsFilter>) => {
        setFilter(state => ({ ...state, ...filter }));
    };

    ///
    const getClassificationIcon = (classification: TrainingIndexTrainingClassification) => {
        var icon: IconProp;
        var color: MprcColors = "primary";

        switch (classification) {
            case TrainingIndexTrainingClassification.Pending:
                icon = faCircleExclamation;
                color = "warning";
                break;
            case TrainingIndexTrainingClassification.InProgress:
                icon = faCirclePlay;
                color = "muted";
                break;
            case TrainingIndexTrainingClassification.Concluded:
                icon = faCircleCheck;
                color = "success";
                break;
            case TrainingIndexTrainingClassification.Exempt:
                icon = faCircleCheck;
                color = "primary";
                break;
        }

        return icon && <MprcText color={color}>
            <FontAwesomeIcon icon={icon} size="lg" title={t(`TrainingIndex.TrainingList.${TrainingIndexTrainingClassification[classification]}Tooltip`)} />
        </MprcText>;
    };

    ///
    const getSubtitle = (item: TrainingIndexTrainingDto) => {
        const items: React.ReactNode[] = [item.trainingContentTypeName];
        var color: MprcColors = "muted";

        switch (item.classification) {
            case TrainingIndexTrainingClassification.Pending:
                color = "warning";
                items.push(item.status == EnrollStatus.WaitingPreApproval
                    ? t("TrainingIndex.TrainingList.WaitingApprovalText")
                    : t("TrainingIndex.TrainingList.PendingText")
                );
                break;
            case TrainingIndexTrainingClassification.InProgress:
                items.push(item.status == EnrollStatus.NotStarted
                    ? t("TrainingIndex.TrainingList.NotStartedText")
                    : t("TrainingIndex.TrainingList.InProgressText")
                );
                break;
            case TrainingIndexTrainingClassification.Concluded:
                color = item.status == EnrollStatus.Reproved
                    ? "error"
                    : "success";

                if (item.finalizationDate != null) {
                    items.push(t("TrainingIndex.TrainingList.FinalizedAtText", { date: Formaters.dayMonth(item.finalizationDate) }));
                }
                break;
            case TrainingIndexTrainingClassification.Exempt:
                color = "primary";
                items.push(<Typography variant='captionHeader'>{t("TrainingIndex.TrainingList.ExemptText")}</Typography>);
                break;
        }

        return <MprcText color={color}><MprcJoinComponents separator="&bull;" components={items} /></MprcText>;
    };


    ///
    const getMenuItems = useCallback((item: TrainingIndexTrainingDto): MprcDropdownItems<MenuOptions> => {

        const group0: MprcDropdownItems<MenuOptions> = [];

        if (item.classification == TrainingIndexTrainingClassification.Pending) {
            if (item.canSelfSubscribe) {
                group0.push({ text: t("TrainingIndex.TrainingList.SelfSubscribeOption"), value: "selfSubscribe" });
            }
            else if (item.canSubscribe) {
                group0.push({ text: t("TrainingIndex.TrainingList.SubscribeOption"), value: "managerSubscribe" });
            }

            if (item.canDoExempt) {
                group0.push({ text: t("TrainingIndex.TrainingList.DoExemptOption"), value: "doExempt" });
            }
        }
        else if (item.classification == TrainingIndexTrainingClassification.InProgress && userId == session.user.userId) {
            group0.push({ text: t("TrainingIndex.TrainingList.GoTrainingOption"), value: "continue" });
        }
        else if (item.classification == TrainingIndexTrainingClassification.Concluded && item.canReview) {
            group0.push({ text: t("TrainingIndex.TrainingList.ReviewOption"), value: "review" });
        }
        else if (item.classification == TrainingIndexTrainingClassification.Exempt) {
            if (item.canDoExempt) {
                group0.push({ text: t("TrainingIndex.TrainingList.UndoExemptOption"), value: "undoExempt" });
            }
            if (item.canSelfSubscribe) {
                group0.push({ text: t("TrainingIndex.TrainingList.SelfSubscribeOption"), value: "selfSubscribe" });
            }
        }

        return MprcDropdownBuildGroupedItems<MenuOptions>([group0]);
    }, [t]);

    ///
    const handleSelectOption = (option: MenuOptions, item: TrainingIndexTrainingDto) => {
        switch (option) {
            case "continue":
            case "review":
                navigate(LmsPaths.BuildEnrollmentViewer(item.trainingType, item.enrollmentId!));
                break;
            case "selfSubscribe":
                setDialog(<TrainingRegistrationDialog
                    trainingId={item.trainingId}
                    onClose={(result) => {
                        if (result?.enrollmentId) {
                            navigate(LmsPaths.BuildEnrollmentViewer(item.trainingType, result?.enrollmentId));
                        }
                        setDialog(null);
                    }}
                />
                );
                break;
            case "managerSubscribe":
                setDialog(<TrainingRegistrationDialog
                    trainingId={item.trainingId}
                    mode="enrollManager"
                    enrollSubordinateId={userId}
                    subtitle={userName}
                    onClose={(result) => {
                        if (result?.enrollmentId) {
                            loadSumary(true);
                            loadData();
                        }
                        setDialog(null);
                    }}
                />
                );
                break;
            case "doExempt":
                setDialog(
                    <DoExemptTraining
                        trainingId={item.trainingId}
                        userId={userId}
                        onAction={(action: string) => {
                            if (action == "successed") {
                                loadSumary(true);
                                loadData();
                            }
                            setDialog(null);
                        }}
                    />);
                break;
            case "undoExempt":
                setDialog(
                    <UndoExemptTraining
                        enrollmentId={item.enrollmentId!}
                        onAction={(action) => {
                            if (action == "successed") {
                                loadSumary(true);
                                loadData();
                            }
                            setDialog(null);
                        }}
                    />);
                break;
        }
    };

    ///
    const openEnrollDetails = (item: TrainingIndexTrainingDto) => {
        setDialog(
            <EnrollmentDetailsDialog
                enrollmentId={item.enrollmentId!}
                subtitle={item.name}
                onClose={() => setDialog(null)}
            />
        );
    };

    ///
    const loadSumary = (refresh: boolean) => {
        api.v1.dashboardTrainingIndex.getUserTrainingsSummary({ userId, topicId: topicId ?? null })
            .then(response => {
                setClassifications(response.data);
                if (response.data.length == 1) {
                    setFilter(state => ({ ...state, classification: response.data[0].classification, pageNumber: 1 }));
                }
                else{
                    setFilter(state => ({ ...state, classification: null, pageNumber: 1 }));
                }
            })
            .catch(e => setViewError(e));
    };

    ///
    useEffect(() => {
        loadSumary(false);
    }, []);

    const loadData = () => {
        setLoading(true);
        api.v1.dashboardTrainingIndex.getUserTrainings({
            userId,
            topicId: topicId ?? null,
            classification: filter.classification,
            pageNumber: filter.pageNumber,
            pageSize: APP_PAGE_SIZE_MEDIUM
        }).then(response => setViewData(response.data))
            .catch(e => setViewError(e))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadData();
    }, [filter]);

    const totalTrainings = classifications.reduce((prev, curr) => prev + curr.trainingsCount, 0);
    if (!loading && viewError == null && totalTrainings == 0) return <></>;

    return <MprcCard>
        <MprcRequestIndicator type="downloading" visible={downloading} />
        <CardContent>
            <MprcContainer>
                <Typography variant="h3">{t("TrainingIndex.TrainingList.Title")}</Typography>
            </MprcContainer>
            {dialog}
            {!loading &&
                <MprcContainer margin="large">
                    <Stack direction="row" spacing={1}>
                        {totalTrainings > 0 && classifications.length > 1 &&
                            <MprcChipChoice
                                label={t(`TrainingIndex.TrainingList.AllFilterOption`, { count: totalTrainings })}
                                selected={filter.classification == null}
                                uppercase
                                onClick={() => handleFilter({ classification: null })}
                            />
                        }
                        {classifications.map(item =>
                            <MprcChipChoice
                                key={item.classification}
                                label={t(`TrainingIndex.TrainingList.${TrainingIndexTrainingClassification[item.classification].toString()}FilterOption`, { count: item.trainingsCount })}
                                selected={filter.classification == item.classification}
                                onClick={() => handleFilter({ classification: item.classification })}
                                uppercase
                            />
                        )}
                    </Stack>
                </MprcContainer>
            }
            {viewError
                ? <MprcRequestErrorReport error={viewError} />
                : loading ?
                    <MprcSpinnerIndicator />
                    : viewData &&
                    <>
                        <Stack direction="column" spacing={2}>
                            {viewData.items.map(item => {
                                var menuItems = getMenuItems(item);
                                return (
                                    <Grid container key={item.trainingId} alignItems="flex-start" spacing={2}>
                                        <Grid>
                                            {getClassificationIcon(item.classification)}
                                        </Grid>
                                        <Grid>
                                            <Avatar variant="rounded" src={item.thumbnailUrl} sx={{ width: 70, height: 48 }} />
                                        </Grid>
                                        <Grid size="grow">
                                            <Stack spacing={menuItems.length > 0 ? 0.5 : 1}>
                                                <Stack direction="row" alignItems="center">
                                                    <MprcListItemTitle
                                                        onClick={
                                                            (item.classification == TrainingIndexTrainingClassification.Concluded && item.enrollmentId != null)
                                                                ? () => openEnrollDetails(item)
                                                                : undefined
                                                        }
                                                    >
                                                        {item.name}
                                                    </MprcListItemTitle>
                                                    {menuItems.length > 0 &&
                                                        <MprcIconButtonMoreOptionsDropdown
                                                            color="muted"
                                                            onChange={(selection) => handleSelectOption(selection, item)}
                                                            items={menuItems}
                                                        />
                                                    }
                                                </Stack>
                                                <Typography variant="caption">
                                                    {getSubtitle(item)}
                                                </Typography>
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                );
                            }
                            )}
                        </Stack>
                        <DashPagination
                            pageNumber={viewData.pageNumber}
                            totalPages={viewData.totalPages}
                            onSetPageNumber={(page) => handleFilter({ pageNumber: page })}
                            onExportData={handleExportExcel}
                        />
                    </>
            }
        </CardContent>
    </MprcCard >
        ;
};
